/* ========================
 * AddSnippetModal.jsx - Modal for adding or editing snippets
 * Ensures snippets (for tags) are loaded if not already.
 * ======================== */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Tag as TagIcon, Plus } from 'lucide-react';
import { useSnippets } from '@/managers/SnippetsManager';
import { useTheme } from '@/managers/ThemeProvider';
import { HousePortal } from '@/App';

const AddSnippetModal = ({
  content = '', // Used for pre-filling content when invoked from 'Save As Snippet'
  language: initialLanguage = '', // Used for pre-filling language
  isOpen, // This prop now primarily signals to parent that modal *should* be open
  onClose,
  existingSnippet = null, // If passed, modal is in "edit" mode
}) => {
  const [name, setName] = useState('');
  const [snippetContent, setSnippetContent] = useState('');
  const [language, setLanguage] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);


  const {
    addSnippet,
    updateSnippet,
    getAllTags,
    loadSnippets,
    isSnippetsLoaded,
    rawSnippets
  } = useSnippets();

  const [showTagSuggestions, setShowTagSuggestions] = useState(false);

  const tagInputRef = useRef(null);
  const nameInputRef = useRef(null);

  const { isLightTheme } = useTheme();

  /* ========================
   * availableTags - Memoized function to get all unique tags
   * ======================== */
  const availableTags = useCallback(() => {
    return getAllTags();
  }, [getAllTags]);

  const filteredSuggestedTags = availableTags().filter(
    (tag) =>
      tag.toLowerCase().includes(tagInput.toLowerCase()) &&
      !tags.includes(tag)
  ).slice(0, 10);

  /* ========================
   * useEffect - Loads snippets when modal opens if not already loaded
   * ======================== */
  useEffect(() => {
    if (isOpen && !isSnippetsLoaded && (!rawSnippets || rawSnippets.length === 0)) {
      loadSnippets();
    }
  }, [isOpen, isSnippetsLoaded, loadSnippets, rawSnippets]);

  /* ========================
   * useEffect - Initializes form fields and sets focus when modal opens
   * ======================== */
  useEffect(() => {
    if (isOpen) {
      setError('');
      setIsSaving(false);

      if (existingSnippet) {
        setName(existingSnippet.name || '');
        setSnippetContent(existingSnippet.code || '');
        setLanguage(existingSnippet.language || '');
        setTags(Array.isArray(existingSnippet.tags) ? existingSnippet.tags : []);
        setNotes(existingSnippet.notes || '');
      } else {
        const contentToUse = content || '';
        let firstLine = contentToUse.split('\n')[0].trim();
        if (firstLine.length > 70) {
            firstLine = firstLine.substring(0, 70) + "...";
        }
        const cleanedFirstLine = firstLine.replace(/^#+\s*/, '');

        setName(cleanedFirstLine);
        setSnippetContent(contentToUse);
        setLanguage(initialLanguage || '');
        setTags([]);
        setNotes('');
      }
      setTagInput('');

      // Set focus to the name input after render cycle
      setTimeout(() => {
        if (nameInputRef.current) {
          nameInputRef.current.focus();
          // Select text only if it's a new snippet and the name was pre-filled (i.e., not empty)
          if (!existingSnippet && nameInputRef.current.value) {
            nameInputRef.current.select();
          }
        }
      }, 100);
    }
  }, [isOpen, existingSnippet, content, initialLanguage]); // Removed 'name' from dependencies

  /* ========================
   * useEffect - Controls tag suggestions visibility
   * ======================== */
  useEffect(() => {
    setShowTagSuggestions(!!tagInput && filteredSuggestedTags.length > 0);
  }, [tagInput, filteredSuggestedTags]);

  /* ========================
   * useEffect - Handles Escape key for modal close
   * ======================== */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  /* ========================
   * handleSave - Saves or updates snippet to Firestore
   * ======================== */
  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);

    if (!name.trim()) {
      setError('Name is required');
      setIsSaving(false);
      return;
    }
    if (!snippetContent.trim()) {
      setError('Content is required');
      setIsSaving(false);
      return;
    }

    try {
      const snippetData = {
        code: snippetContent,
        language: language || 'plaintext',
        name: name.trim(),
        tags,
        notes: notes,
      };

      if (existingSnippet && existingSnippet.id) {
        await updateSnippet(existingSnippet.id, snippetData);
      } else {
        await addSnippet(
          snippetData.code,
          snippetData.language,
          snippetData.name,
          snippetData.tags,
          snippetData.notes
        );
      }
      onClose();
    } catch (err) {
      console.error("Failed to save snippet:", err);
      setError(err.message || 'Failed to save snippet. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  /* ========================
   * addTag - Adds a new tag to the snippet
   * ======================== */
  const addTag = (tag) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags((prev) => [...prev, trimmedTag]);
    }
    setTagInput('');
    setShowTagSuggestions(false);
    if(tagInputRef.current) tagInputRef.current.focus();
  };

  /* ========================
   * removeTag - Removes a tag from the snippet
   * ======================== */
  const removeTag = (tagToRemove) => {
    setTags((prev) => prev.filter((tag) => tag !== tagToRemove));
  };

  /* ========================
   * handleTagKeyDown - Handles key presses in the tag input field
   * ======================== */
  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === ',' && tagInput.trim()) {
        e.preventDefault();
        addTag(tagInput.replace(/,$/, ''));
    } else if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      e.preventDefault();
      removeTag(tags[tags.length - 1]);
    } else if (e.key === 'ArrowDown' && showTagSuggestions && filteredSuggestedTags.length > 0) {
        e.preventDefault();
        const firstSuggestionButton = document.querySelector('#tag-suggestion-0');
        if (firstSuggestionButton) firstSuggestionButton.focus();

    }
  };

  /* ========================
   * handleSuggestionClick - Handles selection of a suggested tag
   * ======================== */
  const handleSuggestionClick = (tag) => {
    addTag(tag);
    if (tagInputRef.current) {
        tagInputRef.current.focus();
    }
  };


  if (!isOpen) return null;

  return (
    <HousePortal>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div
          id="add-snippet-modal"
          className={`w-full max-w-lg m-auto overflow-hidden rounded-xl shadow-xl flex flex-col
            ${
              isLightTheme
                ? 'bg-white border border-border-color'
                : 'bg-gradient-surface border border-white/10'
            }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div
            className={`flex items-center justify-between p-4 shrink-0 ${
              isLightTheme
                ? 'border-b border-border-color'
                : 'border-b border-white/10'
            }`}
          >
            <h3
              className={`text-base font-semibold ${
                isLightTheme ? 'text-black' : 'text-on-surface'
              }`}
            >
              {existingSnippet ? 'Edit Snippet' : 'Save New Snippet'}
            </h3>
            <button
              onClick={onClose}
              disabled={isSaving}
              className={`p-2 rounded-lg transition-colors
                ${isLightTheme ? 'hover:bg-gray-200 text-black/50' : 'hover:bg-white/10 text-on-surface-dim'}
                disabled:opacity-50`}
              aria-label="Close modal"
            >
              <X size={16} />
            </button>
          </div>

          {/* Modal Form */}
          <form onSubmit={handleSave} className="p-4 space-y-4 overflow-y-auto styled-scrollbar flex-1">
            {/* Name Input */}
            <div>
              <label
                htmlFor="snippet-name"
                className={`block text-xs font-medium mb-1 tracking-wide ${
                  isLightTheme ? 'text-black/60' : 'text-on-surface-dim'
                }`}
              >
                NAME
              </label>
              <input
                id="snippet-name"
                ref={nameInputRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`w-full rounded-lg px-3 py-2 text-sm placeholder:text-on-surface-dim/50
                  focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-150
                  ${
                    isLightTheme
                      ? 'bg-gray-100 border border-gray-300 text-black focus:border-primary/50'
                      : 'bg-black/20 border border-white/10 text-on-surface focus:border-primary'
                  }`}
                placeholder="e.g., React useEffect Hook"
              />
            </div>

            {/* Content Input */}
            <div>
              <label
                htmlFor="snippet-content"
                className={`block text-xs font-medium mb-1 tracking-wide ${
                  isLightTheme ? 'text-black/60' : 'text-on-surface-dim'
                }`}
              >
                CONTENT
              </label>
              <textarea
                id="snippet-content"
                value={snippetContent}
                onChange={(e) => setSnippetContent(e.target.value)}
                className={`w-full rounded-lg px-3 py-2 text-sm placeholder:text-on-surface-dim/50
                  focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-150
                  min-h-[120px] max-h-[300px] font-mono
                  ${
                    isLightTheme
                      ? 'bg-gray-100 border border-gray-300 text-black focus:border-primary/50'
                      : 'bg-black/20 border border-white/10 text-on-surface focus:border-primary'
                  }`}
                placeholder="Paste your code or text here..."
              />
            </div>
             {/* Language Input (Optional) */}
            <div>
              <label
                htmlFor="snippet-language"
                className={`block text-xs font-medium mb-1 tracking-wide ${
                  isLightTheme ? 'text-black/60' : 'text-on-surface-dim'
                }`}
              >
                LANGUAGE (OPTIONAL)
              </label>
              <input
                id="snippet-language"
                type="text"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className={`w-full rounded-lg px-3 py-2 text-sm placeholder:text-on-surface-dim/50
                  focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-150
                  ${
                    isLightTheme
                      ? 'bg-gray-100 border border-gray-300 text-black focus:border-primary/50'
                      : 'bg-black/20 border border-white/10 text-on-surface focus:border-primary'
                  }`}
                placeholder="e.g., javascript, python, html (defaults to plaintext)"
              />
            </div>


            {/* Notes Input */}
            <div>
              <label
                htmlFor="snippet-notes"
                className={`block text-xs font-medium mb-1 tracking-wide ${
                  isLightTheme ? 'text-black/60' : 'text-on-surface-dim'
                }`}
              >
                NOTES (OPTIONAL)
              </label>
              <textarea
                id="snippet-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={`w-full rounded-lg px-3 py-2 text-sm placeholder:text-on-surface-dim/50
                  focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-150
                  min-h-[80px] max-h-[200px]
                  ${
                    isLightTheme
                      ? 'bg-gray-100 border border-gray-300 text-black focus:border-primary/50'
                      : 'bg-black/20 border border-white/10 text-on-surface focus:border-primary'
                  }`}
                placeholder="Add any relevant notes or context..."
              />
            </div>

            {/* Tags Input */}
            <div className="relative">
              <label
                htmlFor="snippet-tags"
                className={`block text-xs font-medium mb-1 tracking-wide ${
                  isLightTheme ? 'text-black/60' : 'text-on-surface-dim'
                }`}
              >
                TAGS (OPTIONAL)
              </label>
              <div
                className={`min-h-[40px] w-full rounded-lg px-2 py-1.5 flex flex-wrap gap-1.5 items-center
                  focus-within:ring-2 focus-within:ring-primary/30 transition-all duration-150
                  ${
                    isLightTheme
                      ? 'bg-gray-100 border border-gray-300 focus-within:border-primary/50'
                      : 'bg-black/20 border border-white/10 focus-within:border-primary'
                  }`}
              >
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className={`inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 rounded text-xs group transition-colors
                      ${isLightTheme ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-white/10 text-on-surface-dim hover:bg-white/20'}`}
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className={`p-0.5 rounded-full ${isLightTheme ? 'hover:text-red-500' : 'hover:text-error'}`}
                      aria-label={`Remove tag ${tag}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <input
                  id="snippet-tags"
                  ref={tagInputRef}
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  onFocus={() => setShowTagSuggestions(!!tagInput && filteredSuggestedTags.length > 0)}
                  onBlur={() => setTimeout(() => setShowTagSuggestions(false), 150)}
                  className={`flex-1 min-w-[100px] bg-transparent text-sm placeholder:text-on-surface-dim/50 focus:outline-none
                    ${isLightTheme ? 'text-black' : 'text-on-surface'}`}
                  placeholder={tags.length === 0 ? 'Add tags (comma or Enter)' : ''}
                />
              </div>

              {showTagSuggestions && (
                <div
                  className={`absolute z-20 mt-1 w-full rounded-lg shadow-xl max-h-[150px] overflow-y-auto styled-scrollbar
                    ${
                      isLightTheme
                        ? 'bg-white border border-gray-300'
                        : 'bg-surface-dark border border-white/10'
                    }`}
                >
                  {filteredSuggestedTags.map((tag, index) => (
                    <button
                      id={`tag-suggestion-${index}`}
                      key={tag}
                      type="button"
                      onClick={() => handleSuggestionClick(tag)}
                      onMouseDown={(e) => e.preventDefault()}
                      className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors
                        ${isLightTheme ? 'text-black hover:bg-gray-100' : 'text-on-surface hover:bg-white/10'}`}
                    >
                      <TagIcon size={12} className="text-on-surface-dim shrink-0" />
                      {tag}
                    </button>
                  ))}
                  {tagInput && !filteredSuggestedTags.some(t => t.toLowerCase() === tagInput.trim().toLowerCase()) && !tags.includes(tagInput.trim()) && (
                     <button
                        type="button"
                        onClick={() => addTag(tagInput)}
                        onMouseDown={(e) => e.preventDefault()}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors
                                    ${isLightTheme ? 'text-black hover:bg-gray-100' : 'text-on-surface hover:bg-white/10'}`}
                    >
                        <Plus size={12} className="text-on-surface-dim shrink-0" />
                        Create new tag: "{tagInput.trim()}"
                    </button>
                  )}
                </div>
              )}
            </div>

            {error && (
              <div
                className={`text-sm p-3 rounded-lg border animate-fadeIn
                  ${
                    isLightTheme
                      ? 'text-red-700 bg-red-50 border-red-300'
                      : 'text-error bg-error/10 border-error/30'
                  }`}
              >
                {error}
              </div>
            )}

            {/* Modal Footer/Actions */}
            <div className="flex justify-end gap-2 pt-2 shrink-0">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
                  ${
                    isLightTheme
                      ? 'hover:bg-gray-100 text-black/60'
                      : 'hover:bg-white/10 text-on-surface-dim'
                  } disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors shadow-button hover:shadow-md
                  min-w-[120px]
                  ${
                    isLightTheme
                      ? 'bg-primary hover:bg-primary/90'
                      : 'bg-primary hover:bg-primary-dark'
                  } disabled:opacity-50 disabled:bg-primary/50`}
              >
                {isSaving
                    ? (existingSnippet ? 'Saving...' : 'Adding...')
                    : (existingSnippet ? 'Save Changes' : 'Add Snippet')
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </HousePortal>
  );
};

export default AddSnippetModal;